import { Fragment, useEffect, useState } from 'react';
import { tradeApi } from './api.js';
import AnswerField from './AnswerField.jsx';
import { FIELDS, questionsFor, money, showAnswer, TAX_QUESTIONS, typeLabels, taxLabel } from './fields.js';

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function StatusTag({ status }) {
  const tone = status === 'CFP Communication Required' ? 'is-warn'
    : status === 'Advisor Responded' ? 'is-info'
    : status === 'Completed' ? 'is-done'
    : status === 'Cancelled' ? 'is-muted' : '';
  const label = status === 'CFP Communication Required' ? 'Needs your input' : status;
  return <span className={'tt-status ' + tone}>{label}</span>;
}

/** What the advisor said about one account on an initial allocation, as it reads. */
export function accountSaid(a) {
  const said = Object.entries(a.answers || {})
    .filter(([f]) => f !== 'Managed_By__c' && FIELDS[f])
    .map(([f, v]) => `${FIELDS[f].label}: ${showAnswer(f, v)}`);
  for (const p of a.kindAnswers || []) said.push(`${p.label}: ${p.value}`);
  return said;
}

/** One request: what was asked, the conversation with Investment Management, and the advisor's answer. */
export default function RequestDetail({ id, navigate, onLoaded }) {
  const [state, setState] = useState({ state: 'loading' });
  const [message, setMessage] = useState('');
  const [changes, setChanges] = useState({});
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [answered, setAnswered] = useState(null);

  async function load() {
    const res = await tradeApi.request(id);
    if (!res.ok) { setState({ state: 'failed', error: res.body.error }); return; }
    setState({ state: 'ready', ...res.body });
    if (onLoaded) onLoaded(res.body.request);
  }
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.state === 'loading') {
    return <div className="tt-loading"><div className="load-spinner" /><p>Reading the request from Salesforce…</p></div>;
  }
  if (state.state === 'failed') {
    return <div className="tt-page"><div className="tt-banner is-error" role="alert"><strong>The request could not be opened.</strong> {state.error}</div></div>;
  }

  const { request, messages, canReply, replyFields, picklists, salesforceUrl } = state;
  const cases = state.cases || [];
  const answers = request.answers || {};
  const asked = questionsFor(request.types, request.accounts);
  const editable = [...new Set([...asked, ...TAX_QUESTIONS])].filter(f => replyFields.includes(f) && FIELDS[f]);
  const current = f => (f in changes ? changes[f] : answers[f] ?? '');

  async function sendReply() {
    if (!message.trim()) { setRefusal({ error: 'Write a reply to Investment Management before sending.' }); return; }
    setSending(true);
    setRefusal(null);
    const changed = {};
    for (const [f, v] of Object.entries(changes)) {
      if (String(v ?? '') !== String(answers[f] ?? '')) changed[f] = v === '' ? null : v;
    }
    const res = await tradeApi.reply(id, { message, changes: changed });
    setSending(false);
    if (!res.ok) { setRefusal({ error: res.body.error, field: res.body.field }); return; }
    setAnswered(res.body);
    setMessage('');
    setChanges({});
    load();
  }

  // In the ticket's own order, leaving out blanks and boxes nobody ticked.
  const shown = Object.keys(FIELDS).filter(f => f in answers && answers[f] !== null && answers[f] !== '' && answers[f] !== false
    && !['Description__c', 'Special_Instructions__c'].includes(f));

  return (
    <div className="tt-page">
      <header className="tt-page-head">
        <div>
          <p className="tt-kicker">{typeLabels(request.types)} · {request.household}</p>
          <h1>{request.name} <StatusTag status={request.status} /></h1>
          <p className="tt-muted">Sent {when(request.createdDate)} by {request.createdBy}{request.source ? ` through ${request.source}` : ''}
            {request.assignedTrader && <> · with {request.assignedTrader} in Investment Management</>}
            {request.replaces && <> · replaces <a href={`/trade/requests/${request.replaces}`} onClick={e => { e.preventDefault(); navigate(`/trade/requests/${request.replaces}`); }}>{request.replacesName}</a></>}
          </p>
        </div>
        <button className="btn btn-light" onClick={() => navigate('/trade/requests')}>My requests</button>
      </header>

      {answered && (
        <div className="tt-banner is-ok" role="status">
          Your answer is with Investment Management, and the request is back in their queue{answered.changed && answered.changed.length ? ` with ${answered.changed.length === 1 ? 'one change' : `${answered.changed.length} changes`}` : ''}.
        </div>
      )}

      <section className="tt-card">
        <h2>Conversation with Investment Management</h2>
        {messages.length === 0 && <p className="tt-muted">Nothing yet. If Investment Management needs anything, it appears here.</p>}
        <ol className="tt-thread">
          {messages.map(m => (
            <li key={m.id} className={m.direction === 'To advisor' ? 'is-them' : 'is-me'}>
              <div className="tt-thread-meta">{m.author} · {when(m.createdDate)}</div>
              <div className="tt-thread-body tt-pre">{m.body}</div>
              {m.changes && <div className="tt-thread-changes tt-pre">{m.changes}</div>}
            </li>
          ))}
        </ol>

        {canReply && (
          <div className="tt-reply">
            {refusal && <div className="tt-banner is-error" role="alert"><strong>Not sent.</strong> {refusal.error}</div>}
            <div className="tt-field is-wide">
              <label htmlFor="tt-reply">Your answer</label>
              <textarea id="tt-reply" rows={4} value={message} onChange={e => setMessage(e.target.value)} />
            </div>
            {editable.length > 0 && (
              <details className="tt-more">
                <summary>Change answers on the request</summary>
                <p className="tt-muted">Only answers Salesforce still allows to change are here. The accounts, the request type and the instructions are fixed once a request is sent; to change those, replace the request.</p>
                <div className="tt-grid">
                  {editable.map(f => (
                    <AnswerField key={f} field={f} value={current(f)} onChange={(field, v) => setChanges(c => ({ ...c, [field]: v }))}
                      picklists={picklists} invalid={refusal && refusal.field === f} />
                  ))}
                </div>
              </details>
            )}
            <div className="tt-actions">
              <button className="btn btn-primary" disabled={sending} onClick={sendReply}>{sending ? 'Sending…' : 'Send answer'}</button>
              <button className="btn btn-light" disabled={sending} onClick={() => navigate(`/trade/requests/${id}/replace`)}>Replace the request instead</button>
            </div>
          </div>
        )}
      </section>

      {cases.length > 0 && (
        <section className="tt-card">
          <h2>Cases opened from this request</h2>
          <ul className="tt-list">
            {cases.map(c => (
              <li key={c.id}>
                {salesforceUrl ? <a href={`${salesforceUrl}/${c.id}`} target="_blank" rel="noopener noreferrer"><span className="tt-mono">{c.number}</span></a> : <span className="tt-mono">{c.number}</span>}
                {' '}{c.re || c.subject}{c.type ? <span className="tt-muted"> · {c.type}{c.subtype ? ` (${c.subtype})` : ''}</span> : null}
                <span className="tt-tag">{c.status}</span>{c.owner ? <span className="tt-muted"> · {c.owner}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="tt-card">
        <h2>What was asked</h2>
        <dl className="tt-summary">
          <dt>Accounts</dt>
          <dd>
            <ul className="tt-plain">
              {request.accounts.map(a => {
                const said = accountSaid(a);
                return (
                  <li key={a.id}>
                    {a.name} · {a.type} · {taxLabel(a) ? `${taxLabel(a).toLowerCase()} · ` : ''}{money(a.value)} · IPS {a.ipsModel} · risk {a.riskProfile}
                    {a.tamaracModel ? ` · Tamarac ${a.tamaracModel}` : ''}{a.managedBy && a.managedBy !== 'Pure' ? ` · managed by ${a.managedBy}` : ''}
                    {said.length > 0 && <div className="tt-muted tt-small">{said.join(' · ')}</div>}
                  </li>
                );
              })}
            </ul>
          </dd>
          <dt>Instructions</dt><dd className="tt-pre">{answers.Description__c || <span className="tt-muted">None beyond the answers above.</span>}</dd>
          {answers.Special_Instructions__c && <><dt>Special instructions</dt><dd className="tt-pre">{answers.Special_Instructions__c}</dd></>}
          {shown.map(f => <Fragment key={f}><dt>{FIELDS[f].label}</dt><dd>{showAnswer(f, answers[f])}</dd></Fragment>)}
        </dl>
      </section>

      {request.summary && (
        <section className="tt-card is-quiet">
          <h2>What Investment Management sees</h2>
          <p className="tt-summary-block">{request.summary}</p>
        </section>
      )}
    </div>
  );
}
